import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { formatTransporterLedgerName } from "../lib/transporterParty";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Creatable party dropdown for freight — transport company or transporter.
 * Bank details live on the party master.
 *
 * role:
 *  - "company" → transport_company_* (freight / bilty payee)
 *  - "transporter" → transporter_* (commission ledger)
 */
export default function TransporterFreightFields({
  slip,
  onSlipChange,
  vehicleNumber = "",
  ownerName = "",
  paymentMode = "",
  disabled = false,
  role = "transporter",
  label,
  hint,
  compact = false,
}) {
  const isCompany = role === "company";
  const partyIdKey = isCompany ? "transport_company_party_id" : "transporter_party_id";
  const defaultLabel = isCompany ? "Transport company" : "Transporter";
  const shortHint = isCompany
    ? "Bilty company we used"
    : "Assigned this truck to us";
  const defaultHint = isCompany
    ? "Transport company on the bilty — freight voucher payee when we pay the company."
    : "Transporter who assigned the truck — commission / assignment ledger.";

  const [parties, setParties] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [comboOpen, setComboOpen] = useState(false);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const partyPaidFull = String(paymentMode || slip.payment_mode || "").toUpperCase() === "PARTY_PAID_FULL";

  useEffect(() => {
    if (partyPaidFull) return undefined;
    let cancelled = false;
    setLoadingList(true);
    api
      .get("/parties/bootstrap", { params: { kind: "TRANSPORTER", active: "1" } })
      .then((r) => {
        if (!cancelled) setParties(r.data?.rows || r.data?.results || []);
      })
      .catch(() => {
        if (!cancelled) setParties([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [partyPaidFull]);

  const applyParty = (party) => {
    if (!party) return;
    const name = party.display_name || "";
    const pan = (party.pan || party.pan_no || "").trim().toUpperCase();
    if (isCompany) {
      const same = Boolean(slip.transporter_same_as_company);
      const next = {
        ...slip,
        transport_company_party_id: party.id,
        transport_company_name: name,
        transport_company_display_name: name,
        bilty_transporter_name: name,
        carrier_name: name,
        transport_company_pan: pan || slip.transport_company_pan || "",
      };
      if (same) {
        next.transporter_party_id = party.id;
        next.transporter_display_name = name;
        next.transporter_owner_name = name;
        next.transporter_name = name;
        next.transporter_pan = pan || slip.transporter_pan || "";
        next.transporter_same_as_company = true;
      }
      onSlipChange(next);
      return;
    }
    onSlipChange({
      ...slip,
      transporter_party_id: party.id,
      transporter_display_name: name,
      transporter_owner_name: name,
      transporter_name: name,
      transporter_pan: pan || slip.transporter_pan || "",
      transporter_same_as_company: Boolean(
        slip.transport_company_party_id && String(slip.transport_company_party_id) === String(party.id),
      ),
    });
  };

  const clearParty = () => {
    if (isCompany) {
      const next = {
        ...slip,
        transport_company_party_id: null,
        transport_company_name: "",
        transport_company_display_name: "",
        bilty_transporter_name: "",
      };
      if (slip.transporter_same_as_company) {
        next.transporter_party_id = null;
        next.transporter_display_name = "";
        next.transporter_owner_name = "";
        next.transporter_name = "";
      }
      next.carrier_name = "";
      onSlipChange(next);
      return;
    }
    onSlipChange({
      ...slip,
      transporter_party_id: null,
      transporter_display_name: "",
      transporter_owner_name: "",
      transporter_name: "",
      transporter_same_as_company: false,
    });
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return parties;
    return parties.filter((p) => String(p.display_name || "").toLowerCase().includes(needle));
  }, [parties, q]);

  const selectedLabel = useMemo(() => {
    const id = slip[partyIdKey];
    if (id) {
      const p = parties.find((x) => String(x.id) === String(id));
      if (p?.display_name) return p.display_name;
    }
    if (isCompany) {
      return (
        slip.transport_company_display_name
        || slip.transport_company_name
        || slip.bilty_transporter_name
        || formatTransporterLedgerName(ownerName)
        || ""
      );
    }
    return (
      slip.transporter_display_name
      || slip.transporter_owner_name
      || slip.transporter_name
      || formatTransporterLedgerName(ownerName)
      || ""
    );
  }, [
    slip,
    partyIdKey,
    isCompany,
    parties,
    ownerName,
  ]);

  const createParty = async (rawName) => {
    const owner = formatTransporterLedgerName(rawName);
    if (!owner) {
      toast.error(`Enter a ${isCompany ? "transport company" : "transporter"} name`);
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.post("/parties/transporter/ensure", {
        owner_name: owner,
        vehicle_number: String(vehicleNumber || "").trim() || undefined,
      });
      setParties((prev) => {
        const exists = prev.some((p) => String(p.id) === String(data.id));
        if (exists) {
          return prev.map((p) => (String(p.id) === String(data.id) ? data : p));
        }
        return [...prev, data];
      });
      applyParty(data);
      setComboOpen(false);
      setQ("");
      toast.success(isCompany ? "Transport company added" : "Transporter added");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (partyPaidFull) {
    return (
      <div className="space-y-2" data-testid={`freight-party-fields-${role}`}>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label || defaultLabel}</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Freight is on the party invoice — no separate transport ledger for this trip.
        </p>
      </div>
    );
  }

  const query = q.trim();
  const exactMatch = parties.some(
    (p) => String(p.display_name || "").toLowerCase() === query.toLowerCase(),
  );

  return (
    <div className={compact ? "space-y-1.5" : "space-y-2"} data-testid={`freight-party-fields-${role}`}>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
            {label || defaultLabel}
          </Label>
          {(slip[partyIdKey] || selectedLabel) && !disabled ? (
            <button
              type="button"
              className="text-[10px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              onClick={clearParty}
            >
              Clear
            </button>
          ) : null}
        </div>
        <Popover open={comboOpen} onOpenChange={setComboOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              disabled={disabled || saving}
              className="w-full justify-between font-normal h-9"
            >
              <span className="truncate">
                {selectedLabel || `Search or add ${isCompany ? "company" : "transporter"}…`}
              </span>
              {loadingList || saving
                ? <Loader2 className="h-4 w-4 shrink-0 animate-spin opacity-50" />
                : <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput placeholder="Search or type new name…" value={q} onValueChange={setQ} />
              <CommandList>
                <CommandEmpty>
                  {query ? (
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm flex items-center gap-2"
                      disabled={saving}
                      onClick={() => createParty(query)}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add “{query}”
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground px-3 py-2 block">Type to search or create.</span>
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {filtered.map((p) => (
                    <CommandItem
                      key={p.id}
                      value={p.display_name}
                      onSelect={() => {
                        applyParty(p);
                        setComboOpen(false);
                        setQ("");
                      }}
                    >
                      {p.display_name}
                    </CommandItem>
                  ))}
                </CommandGroup>
                {query && !exactMatch ? (
                  <div className="border-t p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs justify-start gap-1.5"
                      disabled={saving}
                      onClick={() => createParty(query)}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add “{query}”
                    </Button>
                  </div>
                ) : null}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p className="text-[10px] text-muted-foreground/80 leading-snug">
          {hint || (compact ? shortHint : defaultHint)}
        </p>
      </div>
    </div>
  );
}

/** Checkbox: transporter same as transport company. */
export function TransporterSameAsCompanyField({ slip, onSlipChange, disabled = false, compact = false }) {
  const checked = Boolean(slip.transporter_same_as_company)
    || (
      slip.transport_company_party_id
      && slip.transporter_party_id
      && String(slip.transport_company_party_id) === String(slip.transporter_party_id)
    );

  return (
    <label className="flex items-start gap-2 text-xs cursor-pointer rounded-md border border-border/70 bg-muted/20 px-2.5 py-2">
      <input
        type="checkbox"
        className="mt-0.5 accent-[var(--bansal-sage,#556B2F)]"
        disabled={disabled || !slip.transport_company_party_id}
        checked={checked}
        onChange={(e) => {
          const on = e.target.checked;
          if (on) {
            const name = slip.transport_company_name || slip.transport_company_display_name || "";
            onSlipChange({
              ...slip,
              transporter_same_as_company: true,
              transporter_party_id: slip.transport_company_party_id,
              transporter_name: name,
              transporter_owner_name: name,
              transporter_display_name: name,
              transporter_pan: slip.transport_company_pan || slip.transporter_pan || "",
              carrier_name: name,
            });
          } else {
            onSlipChange({
              ...slip,
              transporter_same_as_company: false,
            });
          }
        }}
      />
      <span>
        <span className="font-medium text-foreground">Transporter same as company</span>
        {!compact ? (
          <span className="block text-[10px] text-muted-foreground mt-0.5">
            When the same party issued the bilty and assigned the truck.
          </span>
        ) : (
          <span className="block text-[10px] text-muted-foreground mt-0.5">
            Same party for bilty and truck assignment
          </span>
        )}
      </span>
    </label>
  );
}
